import React, { useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import ObtieneDatosCookies from "../tools/obtieneDatosCookiesUsuario";

const fechaLocal = (valor = new Date()) => {
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) return "";
  const dos = (n) => String(n).padStart(2, "0");
  return `${fecha.getFullYear()}-${dos(fecha.getMonth() + 1)}-${dos(fecha.getDate())}T${dos(fecha.getHours())}:${dos(fecha.getMinutes())}`;
};

const GenerarPaseEspecial = ({ datos = {}, onClose = () => {} }) => {
  const Usuario = ObtieneDatosCookies();
  const sedeID = Number(Usuario?.sedeID);
  const [isLoading, setIsLoading] = useState(false);
  const [sedeSeleccionada, setSedeSeleccionada] = useState(() =>
    sedeID === 2 ? "Rosario" : sedeID === 3 ? "Recreo" : ""
  );
  const [formData, setFormData] = useState(() => ({
    cadeteDni: String(datos?.cadeteDni ?? ""),
    fechaHoraSalida: fechaLocal(datos?.fechaHoraSalida || new Date()),
    fechaHoraRegreso: fechaLocal(datos?.fechaHoraRegreso || new Date(Date.now() + 60 * 60 * 1000)),
    observacion: "",
    estado: "Pendiente",
  }));

  const handleChange = ({ target: { name, value } }) => {
    setFormData((prev) => {
      const siguiente = { ...prev, [name]: value };
      if (name === "fechaHoraSalida" && value) {
        const regreso = new Date(prev.fechaHoraRegreso);
        const salida = new Date(value);
        if (!prev.fechaHoraRegreso || regreso <= salida) {
          siguiente.fechaHoraRegreso = fechaLocal(new Date(salida.getTime() + 60 * 60 * 1000));
        }
      }
      return siguiente;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isLoading) return;

    const dni = formData.cadeteDni.trim();
    const salida = new Date(formData.fechaHoraSalida);
    const regreso = new Date(formData.fechaHoraRegreso);
    const observacion = formData.observacion.trim();
    const estado = formData.estado;

    if (!/^\d{7,9}$/.test(dni)) {
      return Swal.fire("DNI inválido", "Ingresá un DNI válido.", "warning");
    }
    if (!Number.isFinite(salida.getTime()) || !Number.isFinite(regreso.getTime()) || regreso <= salida) {
      return Swal.fire("Fechas inválidas", "El regreso debe ser posterior a la salida.", "warning");
    }
    if (!observacion) {
      return Swal.fire("Motivo obligatorio", "Indicá el motivo que justifica el permiso excepcional.", "warning");
    }
    if (!Usuario?.destino || !Usuario?.identificacion || ![1, 2, 3].includes(sedeID)) {
      return Swal.fire("Datos incompletos", "No se pudo identificar la dependencia, usuario o sede autorizante.", "error");
    }
    if (!sedeSeleccionada) {
      return Swal.fire("Sede obligatoria", "Seleccioná la sede del cadete.", "warning");
    }

    const confirmacion = await Swal.fire({
      title: estado === "Activo" ? "Confirmar salida inmediata" : "Confirmar permiso pendiente",
      html: `Se solicitará crear un permiso especial para el DNI <b>${dni}</b> en estado <b>${estado}</b>.<br/>No se registrará un presente ni se modificará el franco.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: estado === "Activo" ? "Confirmar salida" : "Generar pendiente",
      cancelButtonText: "Cancelar",
    });
    if (!confirmacion.isConfirmed) return;

    setIsLoading(true);
    try {
      const token = btoa(`${process.env.REACT_APP_KEY}:${process.env.REACT_APP_PASS}`);
      const payload = {
        cadeteDni: Number(dni),
        fechaHoraSalida: salida.toISOString(),
        fechaHoraRegreso: regreso.toISOString(),
        dependenciaAutorizanteId: Usuario.destino,
        usuarioAutorizanteId: Usuario.identificacion,
        sede: sedeSeleccionada,
        tipo: "Permiso Especial",
        observacion,
        estado,
      };
      const { data } = await axios.post(
        `${process.env.REACT_APP_BASEURL}NCadetes/creaPaseDeSalida/`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );
      if (!data?.success) {
        return Swal.fire("No se pudo crear", data?.message || "Verificá si existe otro pase pendiente o activo.", "error");
      }
      // El servidor es la fuente de verdad sobre el estado realmente registrado.
      const estadoRegistrado = data?.nuevoPaseDeSalida?.estado;
      if (estadoRegistrado && estadoRegistrado !== estado) {
        await Swal.fire(
          "Permiso creado con otro estado",
          `El servidor registró el permiso como ${estadoRegistrado}, aunque solicitaste ${estado}. Revisá el endpoint de creación.`,
          "warning"
        );
      } else {
        await Swal.fire(
          "Permiso generado",
          estadoRegistrado
            ? `Se creó el permiso especial en estado ${estadoRegistrado} para el DNI ${dni}.`
            : `Se creó el permiso para el DNI ${dni}. Verificá su estado en el listado de pases.`,
          "success"
        );
      }
      onClose();
    } catch (error) {
      console.error("Error al crear permiso especial:", error);
      Swal.fire("Error", error?.response?.data?.message || "No se pudo generar el permiso especial.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const campo = { display: "flex", flexDirection: "column", width: "100%", gap: "6px", minWidth: 0 };
  const entrada = { width: "100%", boxSizing: "border-box", minWidth: 0 };

  return (
    <div style={{ width: "100%", maxWidth: "580px", margin: "0 auto", padding: "8px 0", display: "block", boxSizing: "border-box", color: "#212529" }}>
      <h5 className="mb-2">Permiso especial excepcional</h5>
      <div className="alert alert-info py-2" style={{ width: "100%", boxSizing: "border-box" }}>
        Para cadetes de franco o sin presente. No modifica asistencias ni francos.
      </div>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", width: "100%", gap: "14px", alignItems: "stretch" }}>
        <div style={campo}>
          <label htmlFor="especialDni" className="form-label">DNI del cadete</label>
          <input id="especialDni" name="cadeteDni" className="form-control" style={entrada} inputMode="numeric" pattern="[0-9]*" value={formData.cadeteDni} onChange={handleChange} required />
        </div>
        {sedeID === 1 && (
          <div style={campo}>
            <label htmlFor="especialSede" className="form-label">Sede del cadete</label>
            <select id="especialSede" className="form-select" style={entrada} value={sedeSeleccionada} onChange={(e) => setSedeSeleccionada(e.target.value)} required>
              <option value="">Seleccionar sede</option>
              <option value="Rosario">Rosario</option>
              <option value="Recreo">Recreo</option>
            </select>
          </div>
        )}
        <div style={campo}>
          <label htmlFor="especialSalida" className="form-label">Fecha y hora de salida</label>
          <input id="especialSalida" name="fechaHoraSalida" type="datetime-local" className="form-control" style={entrada} value={formData.fechaHoraSalida} onChange={handleChange} required />
        </div>
        <div style={campo}>
          <label htmlFor="especialRegreso" className="form-label">Fecha y hora de regreso</label>
          <input id="especialRegreso" name="fechaHoraRegreso" type="datetime-local" className="form-control" style={entrada} value={formData.fechaHoraRegreso} onChange={handleChange} required />
        </div>
        <div style={campo}>
          <label className="form-label">Tipo de pase</label>
          <input className="form-control" style={entrada} value="Permiso Especial" readOnly />
        </div>
        <div style={campo}>
          <label htmlFor="especialEstado" className="form-label">¿Cómo querés registrar el permiso?</label>
          <select id="especialEstado" name="estado" className="form-select" style={entrada} value={formData.estado} onChange={handleChange} required>
            <option value="Pendiente">Dejar pendiente (autorizar salida después)</option>
            <option value="Activo">Salida inmediata (activar ahora)</option>
          </select>
          <small style={{ color: "#6b7280" }}>
            {formData.estado === "Activo"
              ? "Se solicitará al servidor que registre el pase como Activo."
              : "El permiso quedará Pendiente hasta su activación."}
          </small>
        </div>
        <div style={campo}>
          <label htmlFor="especialMotivo" className="form-label">Motivo y justificación</label>
          <textarea id="especialMotivo" name="observacion" className="form-control" style={entrada} rows={3} maxLength={1000} value={formData.observacion} onChange={handleChange} placeholder="Ej.: Fallecimiento de familiar. Autorización excepcional..." required />
        </div>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button className="btn btn-primary" type="submit" disabled={isLoading}>
            {isLoading ? "Generando..." : formData.estado === "Activo" ? "Autorizar salida inmediata" : "Generar permiso pendiente"}
          </button>
          <button className="btn btn-outline-secondary" type="button" onClick={onClose} disabled={isLoading}>Cancelar</button>
        </div>
      </form>
    </div>
  );
};

export default GenerarPaseEspecial;
